"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Terminal, Loader, CircleCheck, CircleAlert, Cpu, Network } from "lucide-react";
export default function StartYourActivation() {
  return <section data-section-id="223804" id="lets-build-your-vision" className="bg-background text-foreground relative overflow-hidden py-24 px-4 sm:px-6 lg:px-8">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-secondary/20 to-transparent opacity-50" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-secondary/20 to-transparent opacity-50" />
        <div className="absolute top-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/10 to-transparent opacity-50" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-secondary/5 rounded-lg blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-lg blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-secondary/10 rounded-md mb-4 border border-secondary/20">
            <Network className="size-6 text-secondary" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tight mb-4 text-primary">
            Let’s Build Your Vision
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-default text-lg">
            From initial floor plan design to the final craftsman finish, share your project details to begin your home transformation.
          </p>
        </div>

        <Card className="bg-card/40 backdrop-blur-xl border-border shadow-xl relative overflow-hidden rounded-md">
          {}
          <div className="absolute inset-0 border border-border rounded-md pointer-events-none" />
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-secondary/50 to-transparent opacity-50" />

          <CardHeader className="border-b border-border/50 bg-muted/20 pb-6">
            <CardTitle className="flex items-center gap-2 font-default text-xl text-primary">
              <Terminal className="size-5 text-secondary" />
              <span>Consultation Request</span>
            </CardTitle>
            <CardDescription className="font-default text-sm text-muted-foreground">
              Please provide your residential project parameters...
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-8">
            <FormProvider formKey="vision_form_start_your_project" formId="vision_form_start_your_project" sectionName="StartYourActivation" defaultValues={{
            name: "",
            company: "",
            email: "",
            phone: "",
            services: "",
            details: ""
          }} validationRules={{
            name: {
              required: true
            },
            company: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            services: {
              required: true
            },
            details: {}
          }} submitText="Request Consultation" successMessage="Project inquiry received. Our design team is reviewing your vision and will contact you to schedule a walkthrough.">
              <ActivationFormContent />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function ActivationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="relative">
          <div className="absolute inset-0 bg-secondary/20 rounded-md blur-xl animate-pulse" />
          <div className="size-20 rounded-md bg-background border border-secondary/50 flex items-center justify-center relative z-10 shadow-lg">
            <CircleCheck className="size-10 text-secondary" />
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold font-default text-primary">Inquiry Received</h3>
          <p className="text-muted-foreground font-default text-base max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 font-default border-secondary/50 hover:bg-secondary/10 text-secondary rounded-md">
          <Terminal className="size-4 mr-2" />
          New Inquiry
        </Button>
      </div>;
  }
  const inputClasses = "flex w-full rounded-md border-0 border-b border-border bg-background/30 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/40 focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 font-default transition-colors";
  const labelClasses = "font-default text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2 mb-2";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <span className="text-secondary">/</span> Full Name
              </FormLabel>
              <FormControl>
                <input type="text" {...field} className={`${inputClasses} h-10`} placeholder="Enter your name" />
              </FormControl>
              <FormMessage className="font-default text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Project location is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <span className="text-secondary">/</span> Project Location
              </FormLabel>
              <FormControl>
                <input type="text" {...field} className={`${inputClasses} h-10`} placeholder="Denver, CO" />
              </FormControl>
              <FormMessage className="font-default text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <span className="text-secondary">/</span> Email Address
              </FormLabel>
              <FormControl>
                <input type="email" {...field} className={`${inputClasses} h-10`} placeholder="email@example.com" />
              </FormControl>
              <FormMessage className="font-default text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                <span className="text-secondary">/</span> Phone Number
              </FormLabel>
              <FormControl>
                <input type="tel" {...field} className={`${inputClasses} h-10`} placeholder="(303) 000-0000" />
              </FormControl>
              <FormMessage className="font-default text-xs mt-1" />
            </FormItem>} />
      </div>

      <FormField control={control} name="services" rules={{
      required: "Project type is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>
              <span className="text-secondary">/</span> Project Type
            </FormLabel>
            <FormControl>
              <select {...field} className={`${inputClasses} h-10 appearance-none`}>
                <option value="" disabled className="text-muted-foreground">Select a project category...</option>
                {["Kitchen Remodel", "Bathroom Remodel", "Whole Home Renovation", "ADU / Addition", "Decks & Pergolas", "3D Rendering & Design"].map((option, i) => <option key={i} value={option} data-index={i} className="bg-card text-foreground">
                    {option}
                  </option>)}
              </select>
            </FormControl>
            <FormDescription className="font-default text-xs text-muted-foreground/70 mt-2">
              Select the primary focus of your remodeling project.
            </FormDescription>
            <FormMessage className="font-default text-xs mt-1" />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>
              <span className="text-secondary">/</span> Project Vision & Budget
            </FormLabel>
            <FormControl>
              <textarea {...field} className={`${inputClasses} min-h-[120px] resize-y`} placeholder="Tell us about your dream space and any specific budget considerations..." />
            </FormControl>
            <FormMessage className="font-default text-xs mt-1" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive font-default text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 border-t border-border/50 flex justify-end">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto font-default tracking-widest shadow-lg hover:bg-primary/90 transition-all rounded-md bg-primary text-primary-foreground">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : <>
              <Cpu className="mr-2 size-4" />
              Request Consultation
            </>}
        </Button>
      </div>
    </Form>;
}